import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from "@devhop/ui";
import { RiAddLine, RiMore2Line } from "@remixicon/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type {
  PriceInput,
  WorkspaceSnapshot,
} from "../../domain/contracts/live-workspace";
import { type ProductSet, productSetApi } from "../api/product-set-client";
import { workspaceErrorMessage } from "../api/workspace-client";
import { WorkspaceSelect } from "./WorkspaceProductEditor";
import {
  type ProductSetApply,
  WorkspaceProductSetReview,
} from "./WorkspaceProductSetReview";

const key = (pageId: string) => ["workspace-product-sets", pageId] as const;

function validPrice(price: PriceInput) {
  return (
    price.mode === "inherit" || price.mode === "none" || price.value != null
  );
}

export function WorkspaceProductSets({
  pageId,
  workspace,
  selectedVariantIds,
  selectedPrice,
  onApply,
  disabled,
}: {
  pageId: string;
  workspace: WorkspaceSnapshot;
  selectedVariantIds: string[];
  selectedPrice: PriceInput;
  onApply: ProductSetApply;
  disabled: boolean;
}) {
  const client = useQueryClient();
  const nav = useNavigate();
  const query = useQuery({
    queryKey: key(pageId),
    queryFn: () => productSetApi.list(pageId),
    enabled: Boolean(pageId),
  });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [price, setPrice] = useState<PriceInput>(selectedPrice);
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [reviewSet, setReviewSet] = useState<ProductSet | null>(null);
  const variants = [...new Set(selectedVariantIds)];
  const activeItems = variants.map((variantId) => ({
    variantId,
    price,
  }));
  useEffect(() => {
    if (!open) setPrice(selectedPrice);
  }, [open, selectedPrice]);
  const openCreate = () => {
    setName("");
    setPrice(selectedPrice);
    setOpen(true);
  };
  const persist = async () => {
    if (!name.trim() || !activeItems.length || !validPrice(price)) return;
    setSaving(true);
    try {
      const input = {
        pageId,
        name: name.trim(),
        items: activeItems.map((item) => ({
          variantId: item.variantId,
          price,
        })),
      };
      await productSetApi.create(input);
      await client.invalidateQueries({ queryKey: key(pageId) });
      setOpen(false);
      setNotice("ບັນທຶກຊຸດສິນຄ້າແລ້ວ");
    } catch {
      setNotice("ບັນທຶກຊຸດສິນຄ້າບໍ່ສຳເລັດ");
    } finally {
      setSaving(false);
    }
  };
  const applySet: ProductSetApply = async (templateId, preview) => {
    setNotice("");
    const result = await onApply(templateId, preview);
    if (!result) {
      setNotice("ເພີ່ມຊຸດສິນຄ້າບໍ່ສຳເລັດ");
      return false;
    }
    setNotice(
      `ເພີ່ມພ້ອມຂາຍ ${result.added?.length ?? 0} · ເພີ່ມແຕ່ປິດຂາຍ ${result.prepared?.length ?? 0} · ມີຢູ່ແລ້ວ ${result.skipped?.length ?? 0} · ໃຊ້ບໍ່ໄດ້ ${result.failed?.length ?? 0}`,
    );
    return result;
  };
  return (
    <section
      className="space-y-2 rounded-lg border bg-muted/20 p-3"
      aria-labelledby="workspace-product-sets-heading"
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3
            id="workspace-product-sets-heading"
            className="font-medium text-sm"
          >
            ຊຸດສິນຄ້າ
          </h3>
          <p className="text-muted-foreground text-xs">
            ເກັບ SKU ແລະລາຄາໄວ້ໃຊ້ກັບ Live / Feed ໄດ້
          </p>
        </div>
        <Button
          variant="outline"
          className="min-h-11 shrink-0"
          disabled={
            disabled ||
            !variants.length ||
            !validPrice(selectedPrice)
          }
          onClick={openCreate}
        >
          <RiAddLine size={18} /> ບັນທຶກເປັນຊຸດ
        </Button>
      </div>
      {query.isPending ? (
        <output className="block">ກຳລັງໂຫຼດຊຸດ…</output>
      ) : query.isError ? (
        <div role="alert">
          <p>{workspaceErrorMessage(query.error)}</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            ລອງໃໝ່
          </Button>
        </div>
      ) : query.data?.length ? (
        <div className="grid gap-1.5 sm:grid-cols-2">
          {query.data.map((set) => (
            <div
              key={set.id}
              className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-1.5 rounded-md border bg-background px-2 py-1.5"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-sm">{set.name}</p>
                <p className="truncate text-muted-foreground text-xs">
                  {set.items.length} SKU
                </p>
              </div>
              <Button
                size="sm"
                className="min-h-11"
                disabled={disabled || saving}
                onClick={() => setReviewSet(set)}
              >
                ເພີ່ມຊຸດນີ້
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-11"
                aria-label={`ເປີດຊຸດ ${set.name}`}
                onClick={() =>
                  void nav({
                    to: "/app/product-sets/$id",
                    params: { id: set.id },
                    search: { pageId },
                  })
                }
              >
                <RiMore2Line size={18} />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground text-xs">ຍັງບໍ່ມີຊຸດສິນຄ້າ</p>
      )}
      {notice ? (
        <output className="block text-xs" aria-live="polite">
          {notice}
        </output>
      ) : null}
      {reviewSet ? (
        <WorkspaceProductSetReview
          template={
            query.data?.find((set) => set.id === reviewSet.id) ?? reviewSet
          }
          workspace={workspace}
          onApply={applySet}
          onRefresh={() =>
            Promise.all([
              query.refetch(),
              client.invalidateQueries({
                queryKey: ["workspace", workspace.id],
              }),
            ])
          }
          onClose={() => setReviewSet(null)}
        />
      ) : null}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>ບັນທຶກຊຸດສິນຄ້າ</DialogTitle>
            <DialogDescription>
              ບັນທຶກ {variants.length} SKU ທີ່ເລືອກ
            </DialogDescription>
          </DialogHeader>
          <label htmlFor="workspace-set-name" className="space-y-1 text-sm">
            <span>ຊື່ຊຸດ</span>
            <Input
              id="workspace-set-name"
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <WorkspaceSelect
              label="ລາຄາ"
              value={price.mode}
              onChange={(mode) =>
                setPrice({ mode: mode as PriceInput["mode"] })
              }
              items={[
                ["inherit", "ໃຊ້ໂປຣຂອງງານ"],
                ["sale", "ລາຄາຂາຍຈິງ"],
                ["percent", "ຫຼຸດ %"],
                ["fixed", "ຫຼຸດເປັນກີບ"],
                ["none", "ບໍ່ຫຼຸດລາຄາ"],
              ]}
            />
            {price.mode !== "inherit" && price.mode !== "none" ? (
              <label
                htmlFor="workspace-set-price"
                className="space-y-1 text-sm"
              >
                <span>
                  {price.mode === "percent" ? "ສ່ວນຫຼຸດ (%)" : "ຈຳນວນ (ກີບ)"}
                </span>
                <Input
                  id="workspace-set-price"
                  type="number"
                  min={0}
                  max={price.mode === "percent" ? 100 : 999999999}
                  value={price.value ?? ""}
                  onChange={(event) =>
                    setPrice({
                      ...price,
                      value:
                        event.target.value === ""
                          ? undefined
                          : Number(event.target.value),
                    })
                  }
                />
              </label>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              ຍົກເລີກ
            </Button>
            <Button
              disabled={
                saving ||
                !name.trim() ||
                !activeItems.length ||
                !validPrice(price)
              }
              onClick={() => void persist()}
            >
              ບັນທຶກ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
